// The pack opening simulator: pick a pack, read its odds, rip it open, flip the cards one by one, and watch
// them hop into the deck. What is in a pack is worked out in src/shared/packs.ts; this is only the show.
import { PACKS, PACKS_AFTER_EMPEROR, openPack, packArtPath, packOddsText, packRows, type PackDef } from '../shared/packs.ts';
import { costLabel, isEmperor, rarityLabel, titleCase, type CardDef, cardNo } from '../shared/cards.ts';
import type { Deck } from '../shared/deck.ts';
import { CONFIG } from '../config.ts';
import { CARD_DB } from './data.ts';
import { cardBackSrc, cardFrontSrc, setImg } from './images.ts';
import { el, button, modal, showCardLarge, toast } from './ui.ts';
import { cardRulesNode } from './cardRules.ts';
import { isTouchScreen } from './touch.ts';

export interface PackRun {
  opened: string[]; // pack ids, in the order they were opened (the Emperor Pack first)
}
export const PACKS_PER_DECK = 1 + PACKS_AFTER_EMPEROR;
/** In a pack run's list of opened packs: an opening used up by the cards The Remaining brought with it. */
const AUTOFILL = 'autofill';

export interface PackPickerOptions {
  run: PackRun;
  deck: Deck;
  /** a card has landed in the deck (add it there and update the counts) */
  onLand: (card: CardDef) => void;
  /** where the cards hop to: the deck's card count (or the Emperor count, for the Emperor) */
  target: (card: CardDef) => HTMLElement | null;
  /** every pack of this deck is open */
  onComplete: () => void;
}

const reduced = () => typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
const ms = (n: number) => (reduced() ? Math.round(n * 0.25) : n);
const wait = (n: number) => new Promise((r) => setTimeout(r, ms(n)));
const glowOf = (c: CardDef) => {
  const colors = CONFIG.playEffects.colors as Record<string, string[]>;
  return (isEmperor(c) ? colors.emperor : colors[c.rarity]) ?? ['#ffffff'];
};
const rarityName = (r: string) => titleCase(r.replace(/-/g, ' '));
const pctText = (p: number) => `${Number(p.toFixed(2))}%`;
const HYPE = ['epic', 'mythic', 'legendary', 'grand', 'ancient'];

// pack art that turned out to be missing, so it isn't asked for again
const noArt = new Set<string>();
/**
 * The crimp across the top of every pack, the sealed strip that tears off: fine ridges, a light sheen and a
 * shadowed lower edge. Drawn over pack art too.
 */
const CRIMP = [
  'linear-gradient(180deg, rgb(255 255 255 / 0.22) 0%, rgb(255 255 255 / 0) 35%, rgb(0 0 0 / 0) 70%, rgb(0 0 0 / 0.45) 100%)',
  'repeating-linear-gradient(90deg, rgb(0 0 0 / 0.32) 0 2px, rgb(255 255 255 / 0.2) 2px 4px)',
].join(', ');
/**
 * A pack: its art (public/packs/<id>.png, or the pack's own `art`) if there is any, else a foil drawn in CSS
 * in the pack's two colors.
 */
export function packFoil(pack: PackDef, big = false) {
  const top = el('div', { class: 'pack-top' });
  const foil = el(
    'div',
    { class: `pack-foil${big ? ' is-big' : ''}`, style: { '--c1': pack.colors[0], '--c2': pack.colors[1] } },
    top,
    el(
      'div',
      { class: 'pack-body' },
      el('span', { class: 'pack-emblem', 'aria-hidden': 'true' }, pack.emperor ? '♛' : pack.ancients ? '✦' : '◆'),
      el('span', { class: 'pack-name' }, pack.name.replace(/ Pack$/, '')),
      el('span', { class: 'pack-sub' }, pack.emperor ? '10 cards + Emperor' : '10 cards'),
    ),
  );
  const src = packArtPath(pack);
  if (!noArt.has(src)) {
    const art = el('img', { class: 'pack-art', alt: pack.name, draggable: 'false' });
    art.addEventListener('load', () => {
      foil.classList.add('has-art');
      // the strip that tears off shows the top of the art, with the foil's crimp pressed into it
      top.style.backgroundImage = `${CRIMP}, url("${src}")`;
    });
    art.addEventListener('error', () => {
      noArt.add(src);
      art.remove();
    });
    art.src = src;
    foil.prepend(art);
  }
  return foil;
}

/** Which set's packs the shelf shows (CONFIG.sets). Stays as you left it. */
let shelfSet = 'base';

/** A pack is being opened (from the rip until its cards are in the deck): the shelf can't open over it. */
let opening = false;
export const packOpening = () => opening;

/** The pack shelf: every pack, which ones can be opened now, and how far this deck has got. */
export function showPackPicker(opts: PackPickerOptions) {
  if (opening) return; // one pack at a time: the shelf comes back by itself once this one's cards are in the deck
  const { run } = opts;
  const body = el('div', { class: 'stack pack-picker' });
  let close = () => {};
  const done = run.opened.length;
  const needEmperor = !run.opened.includes('emperor');

  const shelf = () => {
    const progress = el(
      'div',
      { class: 'pack-progress', 'aria-label': `${done} of ${PACKS_PER_DECK} packs opened` },
      Array.from({ length: PACKS_PER_DECK }, (_, i) => {
        // (a pack opening used up by The Remaining's own cards shows as a deep red pip)
        const p = run.opened[i] === AUTOFILL ? { name: "Filled by The Remaining's set", colors: ['#b3121f', '#3a0208'] } : run.opened[i] ? PACKS.find((x) => x.id === run.opened[i]) : null;
        return el('span', { class: `pack-pip${p ? ' is-open' : ''}`, title: p ? p.name : i === 0 ? 'Emperor Pack' : 'Any pack', style: p ? { '--c1': p.colors[0] } : {} });
      }),
    );
    body.replaceChildren(
      el('h2', {}, 'Open Packs'),
      el(
        'p',
        { class: 'muted' },
        needEmperor
          ? 'Every pack deck starts with the Emperor Pack: 10 cards and your Emperor. Then open any 5 more packs (the same one again is fine) for a full 60.'
          : `${PACKS_PER_DECK - done} pack${PACKS_PER_DECK - done === 1 ? '' : 's'} to go. Pick any pack: the same one again is fine. No re-rolls: Clear Deck starts over.`,
      ),
      progress,
      // the set filter: the shelf shows one set's packs at a time
      el(
        'div',
        { class: 'chips set-filter', role: 'group', 'aria-label': 'Which set’s packs to show' },
        Object.entries(CONFIG.sets).map(([id, st]) => {
          const chip = el('button', { class: 'chip', type: 'button', 'aria-pressed': String(shelfSet === id) }, st.name);
          chip.addEventListener('click', () => { shelfSet = id; shelf(); });
          return chip;
        }),
      ),
      packRows().every((row) => !row.some((p) => (p.set ?? 'base') === shelfSet))
        ? el('p', { class: 'empty-note' }, `No ${CONFIG.sets[shelfSet]?.name ?? 'packs'} packs yet: they are on their way.`)
        : el(
        'div',
        { class: 'pack-shelf' },
        // one row per group of packs (the groups are PACK_ROWS in src/shared/packs.ts), the chosen set's only
        packRows().map((row) => row.filter((p) => (p.set ?? 'base') === shelfSet)).filter((row) => row.length).map((row) =>
          el(
            'div',
            { class: 'pack-shelf-row' },
            row.map((p) => {
              const usable = needEmperor ? p.emperor : !p.emperor;
              const b = el(
                'button',
                { class: 'pack-pick', type: 'button', disabled: !usable, 'aria-label': usable ? `${p.name}: see its odds and open it` : `${p.name}: ${p.emperor ? 'you only get one Emperor Pack' : 'open the Emperor Pack first'}` },
                packFoil(p),
              );
              b.addEventListener('click', () => details(p));
              // the pack's name pops up while the cursor is over it (the wrapper, so it shows on greyed-out packs too)
              return el('div', { class: 'pack-pick-wrap' }, b, el('span', { class: 'pack-hover-name', 'aria-hidden': 'true' }, p.name));
            }),
          ),
        ),
      ),
      el('div', { class: 'row-end' }, button('Close', () => close(), 'btn-ghost')),
    );
  };

  const details = (p: PackDef) => {
    const odds = packOddsText(p);
    const table = (title: string, rows: { label: string; pct: number }[], color: (label: string) => string) =>
      el(
        'div',
        { class: 'odds-table' },
        el('h3', {}, title),
        rows.map((r) =>
          el(
            'div',
            { class: 'odds-row' },
            el('span', { class: 'odds-label' }, r.label),
            el('span', { class: 'odds-bar' }, el('span', { style: { width: `${Math.max(2, r.pct)}%`, background: color(r.label) } })),
            el('span', { class: 'odds-pct' }, pctText(r.pct)),
          ),
        ),
      );
    const rarityColor = (r: string) => ((CONFIG.playEffects.colors as Record<string, string[]>)[r] ?? ['#888'])[0];
    const extras = [
      p.emperor ? 'Plus your Emperor, always the last card.' : '',
      p.ancients ? `Plus ${p.ancients.guaranteed} guaranteed Ancient, and a ${p.ancients.extraChance}% chance of a second.` : '',
      p.note ?? '',
    ].filter(Boolean);
    body.replaceChildren(
      el(
        'div',
        { class: 'pack-detail' },
        packFoil(p, true),
        el(
          'div',
          { class: 'stack' },
          el('h2', {}, p.name),
          el('p', { class: 'muted' }, p.blurb),
          el('p', { class: 'odds-note' }, 'Chances are for each card on its own: every card is rolled separately.'),
          table('Rarity', odds.rarity.map((r) => ({ ...r, label: rarityName(r.label) })), (l) => rarityColor(l.toLowerCase().replace(/ /g, '-'))),
          table('Card type', odds.type, () => 'var(--frost)'),
          extras.length ? el('p', { class: 'odds-note' }, extras.join(' ')) : '',
        ),
      ),
      el(
        'div',
        { class: 'row-end' },
        button('← All Packs', shelf, 'btn-ghost'),
        button(`Rip It Open`, () => {
          if (opening) return;
          const opened = openPack(p.id, CARD_DB, opts.deck, Math.random, { noSecrets: run.opened.length + 1 >= CONFIG.secretRares.noSecretsFrom });
          if (!opened) return;
          opening = true;
          run.opened.push(p.id);
          close();
          void ripOpen(p, opened.cards, opts, opened.bonus);
        }, 'btn-primary', { id: 'rip-pack' }),
      ),
    );
  };

  shelf();
  close = modal(body, { wide: true, className: 'pack-modal' });
}

/** The show: shake, rip, cards fly out, flip one at a time, then hop into the deck. */
async function ripOpen(pack: PackDef, cards: CardDef[], opts: PackPickerOptions, bonus: CardDef[] = []) {
  const landed = new Set<number>(); // indexes already in the deck
  const land = (i: number) => {
    if (landed.has(i)) return;
    landed.add(i);
    opts.onLand(cards[i]);
  };

  const foil = packFoil(pack, true);
  const pile = el('div', { class: 'pack-pile', role: 'button', tabindex: '0', 'aria-label': 'Flip the next card' });
  const row = el('div', { class: 'pack-row', style: { '--n': String(cards.length) } });
  const callout = el('div', { class: 'pack-callout', 'aria-live': 'polite' });
  const hint = el('p', { class: 'pack-hint' }, '');
  const revealAll = button('Reveal All', () => void flipAll(), 'btn-ghost', { hidden: true });
  const intoDeck = button('Into the Deck →', () => void hop(), 'btn-primary', { hidden: true, id: 'pack-into-deck' });
  const stage = el(
    'div',
    { class: 'pack-stage', style: { '--c1': pack.colors[0], '--c2': pack.colors[1] } },
    el('div', { class: 'pack-stage-title' }, pack.name),
    el('div', { class: 'pack-center' }, foil, pile, callout),
    row,
    el('div', { class: 'pack-stage-foot' }, hint, revealAll, intoDeck),
  );
  document.body.append(stage);

  // the cards, face down: the least rare at the front of the pile (it flips first), the Emperor at the very back
  const nodes = cards.map((c, i) => {
    const front = el('img', { class: 'pc-face pc-front', alt: c.name, width: '500', height: '700' });
    const back = el('img', { class: 'pc-face pc-back', alt: '', width: '500', height: '700' });
    setImg(front, cardFrontSrc(c.number));
    setImg(back, cardBackSrc());
    const node = el('div', { class: 'pack-card', style: { zIndex: String(cards.length - i), '--glow': glowOf(c)[0] } }, el('div', { class: 'pc-inner' }, back, front));
    // right-click a card that has been flipped to see it up close
    node.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!node.classList.contains('is-face')) return;
      showCardLarge(
        cardFrontSrc(c.number),
        `${cardNo(c)} ${c.name}`,
        `${rarityLabel(c) ? `${rarityLabel(c)} · ` : ''}${titleCase(c.types.join('/'))} · cost ${costLabel(c)} · ${titleCase(c.affiliations.join('/'))}`,
        [],
        { rules: cardRulesNode(c) },
      );
    });
    return node;
  });
  for (let i = 0; i < cards.length; i++) row.append(el('div', { class: 'pack-slot' }));

  // shake…
  await foil.animate(
    [
      { transform: 'rotate(0)' }, { transform: 'rotate(-4deg) scale(1.02)' }, { transform: 'rotate(4deg) scale(1.03)' },
      { transform: 'rotate(-5deg) scale(1.04)' }, { transform: 'rotate(5deg) scale(1.05)' }, { transform: 'rotate(0) scale(1.05)' },
    ],
    { duration: ms(800), easing: 'ease-in-out', fill: 'forwards' },
  ).finished;
  // …rip the top off…
  const top = foil.querySelector('.pack-top') as HTMLElement;
  top.animate(
    [{ transform: 'none', opacity: 1 }, { transform: 'translate(140px,-180px) rotate(38deg)', opacity: 0 }],
    { duration: ms(600), easing: 'cubic-bezier(.2,.7,.3,1)', fill: 'forwards' },
  );
  foil.classList.add('is-torn');
  await wait(250);
  // …and the cards fly out into a pile
  for (let i = nodes.length - 1; i >= 0; i--) {
    pile.append(nodes[i]);
    const spin = (Math.random() - 0.5) * 30;
    nodes[i].animate(
      [
        { transform: `translate(-50%, 10%) scale(.55) rotate(${spin}deg)`, opacity: 0 },
        { transform: `translate(-50%, -140%) scale(.9) rotate(${-spin}deg)`, opacity: 1, offset: 0.55 },
        { transform: `translate(calc(-50% + ${(i % 3) - 1}px), calc(-50% + ${i * -1.5}px)) rotate(${(i % 2 ? 1 : -1) * 1.5}deg)`, opacity: 1 },
      ],
      { duration: ms(520), easing: 'cubic-bezier(.2,.8,.3,1)', fill: 'forwards' },
    );
    await wait(70);
  }
  foil.animate([{ transform: 'scale(1.05)', opacity: 1 }, { transform: 'translateY(120px) scale(.8)', opacity: 0 }], { duration: ms(450), fill: 'forwards' });
  await wait(500);
  hint.textContent = isTouchScreen() ? 'Tap the pile to flip the next card. Press and hold a flipped card to see it up close.' : 'Click the pile (or press Space) to flip the next card. Right-click a flipped card to see it up close.';
  revealAll.hidden = false;
  pile.focus();

  let next = 0;
  let busy = false;
  const flip = async (fast = false) => {
    if (next >= nodes.length) return;
    const i = next++;
    const node = nodes[i];
    const card = cards[i];
    const hype = isEmperor(card) || HYPE.includes(card.rarity);
    const inner = node.querySelector('.pc-inner') as HTMLElement;
    node.style.zIndex = '100';
    // lift, flip, and (for the big ones) a moment in the spotlight
    await inner.animate(
      [{ transform: 'rotateY(0) scale(1)' }, { transform: 'rotateY(90deg) scale(1.12)' }, { transform: 'rotateY(180deg) scale(1)' }],
      { duration: ms(fast ? 260 : 420), easing: 'ease-in-out', fill: 'forwards' },
    ).finished;
    node.classList.add('is-face', `r-${isEmperor(card) ? 'emperor' : card.rarity}`);
    if (!fast) {
      callout.textContent = isEmperor(card) ? `Your Emperor: ${card.name}!` : `${rarityName(card.rarity)}${hype ? '!' : ''}`;
      callout.style.color = glowOf(card)[glowOf(card).length > 1 ? 1 : 0];
      callout.classList.remove('is-on');
      void callout.offsetWidth;
      callout.classList.add('is-on');
      if (hype) {
        node.classList.add('is-hype');
        await node.animate(
          [{ transform: 'translate(-50%,-50%) scale(1)' }, { transform: 'translate(-50%,-50%) scale(1.22)' }, { transform: 'translate(-50%,-50%) scale(1.15)' }],
          { duration: ms(isEmperor(card) || ['legendary', 'grand', 'ancient'].includes(card.rarity) ? 1100 : 700), easing: 'ease-out', fill: 'forwards' },
        ).finished;
      } else await wait(260);
    }
    // into its place in the row (FLIP: measure, move, animate the difference)
    const from = node.getBoundingClientRect();
    node.getAnimations().forEach((a) => a.cancel());
    node.classList.add('in-row');
    node.style.zIndex = '';
    (row.children[i] as HTMLElement).append(node);
    const to = node.getBoundingClientRect();
    node.animate(
      [
        { transform: `translate(${from.left - to.left}px, ${from.top - to.top}px) scale(${from.width / to.width})`, transformOrigin: 'top left' },
        { transform: 'none', transformOrigin: 'top left' },
      ],
      { duration: ms(fast ? 260 : 380), easing: 'cubic-bezier(.2,.8,.3,1)' },
    );
    if (next >= nodes.length) finished();
  };
  const finished = () => {
    revealAll.hidden = true;
    hint.textContent = `All revealed! ${isTouchScreen() ? 'Press and hold' : 'Right-click'} a card to see it up close.`;
    intoDeck.hidden = false;
    intoDeck.focus();
  };
  const step = async () => {
    if (busy) return;
    busy = true;
    await flip();
    busy = false;
  };
  const flipAll = async () => {
    revealAll.hidden = true;
    while (busy) await wait(50);
    busy = true;
    while (next < nodes.length) {
      await flip(true);
      await wait(40);
    }
    busy = false;
  };
  pile.addEventListener('click', () => void step());
  const onKey = (e: KeyboardEvent) => {
    if (document.querySelector('.backdrop')) return; // a card is open up close: the keys are for it
    if (e.key === ' ' || e.key === 'Enter') {
      if (next < nodes.length) {
        e.preventDefault();
        void step();
      }
    }
  };
  window.addEventListener('keydown', onKey);

  const hop = async () => {
    intoDeck.hidden = true;
    hint.textContent = '';
    callout.textContent = '';
    stage.classList.add('is-hopping'); // the backdrop clears so the deck counts below show
    await wait(250);
    const all = nodes.map((node, i) => {
      const from = node.getBoundingClientRect();
      const target = opts.target(cards[i])?.getBoundingClientRect();
      const dx = target ? target.left + target.width / 2 - (from.left + from.width / 2) : 0;
      const dy = target ? target.top + target.height / 2 - (from.top + from.height / 2) : 300;
      return new Promise<void>((resolve) => {
        setTimeout(() => {
          const a = node.animate(
            [
              { transform: 'none', opacity: 1 },
              { transform: `translate(${dx * 0.45}px, ${dy * 0.45 - 140}px) scale(.7) rotate(-8deg)`, opacity: 1, offset: 0.45 },
              { transform: `translate(${dx}px, ${dy}px) scale(.12) rotate(6deg)`, opacity: 0.15 },
            ],
            { duration: ms(640), easing: 'cubic-bezier(.3,.1,.5,1)', fill: 'forwards' },
          );
          a.finished.then(() => {
            land(i);
            resolve();
          });
        }, ms(i * 80));
      });
    });
    await Promise.all(all);
    end();
  };
  const end = () => {
    window.removeEventListener('keydown', onKey);
    cards.forEach((_, i) => land(i)); // nothing is ever lost, whatever was skipped
    // a secret rare was in the pack: the rest of its set goes straight into the deck, in place of one pack opening
    if (bonus.length) {
      for (const c of bonus) opts.onLand(c);
      opts.run.opened.push(AUTOFILL);
      const counts = [...new Set(bonus)].map((c) => `${bonus.filter((b) => b === c).length} ${c.name}`).join(', ');
      toast(`The Remaining's set is complete: ${counts} were added to your deck. That takes the place of one of your packs.`, 'info');
    }
    stage.animate([{ opacity: 1 }, { opacity: 0 }], { duration: ms(200), fill: 'forwards' }).finished.then(() => {
      stage.remove();
      opening = false; // its cards are all in the deck: the shelf may open again
      if (opts.run.opened.length >= PACKS_PER_DECK) opts.onComplete();
      else showPackPicker(opts);
    });
  };
}
